import { findMissionEndDockAction } from "lib/mission/dockingMissionSteps";

export const MISSION_END_STOP_360_NOTE = "__MISSION_END_STOP_360__";

interface Record360ActionLike {
    actionType: string;
    note?: string | null;
    sequenceOrder?: number;
    args?: unknown;
}

const commandOf = (action: Record360ActionLike): string => {
    const args = action.args as { command?: string } | undefined;
    return String(args?.command || "").toLowerCase();
};

export const isRecord360Start = (action: Record360ActionLike): boolean =>
    action.actionType === "Record360" && commandOf(action) === "start";

export const isRecord360Stop = (action: Record360ActionLike): boolean =>
    action.actionType === "Record360" && commandOf(action) === "stop";

export const isMissionEndStop360Action = (action: Record360ActionLike): boolean =>
    isRecord360Stop(action) && (action.note ?? "") === MISSION_END_STOP_360_NOTE;

export const findMissionEndStop360Action = <T extends Record360ActionLike>(
    actions: T[]
): T | undefined => actions.find(isMissionEndStop360Action);

/** Pin new steps before: end Stop 360 (if any), else end dock. */
export const findRecord360PlacementPin = <T extends Record360ActionLike>(
    actions: T[],
    endDock?: T
): T | undefined => findMissionEndStop360Action(actions) ?? endDock;

/** sequenceOrder for a new mission-end Stop 360 (before end dock when present). */
export const missionEndStop360SequenceOrder = (
    actions: Record360ActionLike[],
    excludeActionId?: string
): number => {
    const endDock = findMissionEndDockAction(actions);
    if (endDock != null && endDock.sequenceOrder != null) {
        return endDock.sequenceOrder;
    }
    const others = excludeActionId
        ? actions.filter((a) => (a as { id?: string }).id !== excludeActionId)
        : actions;
    if (others.length === 0) return 0;
    return Math.max(...others.map((a) => a.sequenceOrder ?? 0)) + 1;
};

export const nextRecord360Command = (
    actions: Record360ActionLike[],
    insertionIndex: number | null
): "start" | "stop" => {
    const sorted = actions
        .slice()
        .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
    const pin = findMissionEndStop360Action(sorted);
    const insertAt =
        insertionIndex ?? pin?.sequenceOrder ?? sorted.length;
    let depth = 0;
    for (const action of sorted) {
        if ((action.sequenceOrder ?? 0) >= insertAt) break;
        if (isMissionEndStop360Action(action)) continue;
        if (action.actionType !== "Record360") continue;
        const command = commandOf(action);
        if (command === "start") depth += 1;
        else if (command === "stop") depth = Math.max(0, depth - 1);
    }
    return depth > 0 ? "stop" : "start";
};
